import { useState, useEffect, useCallback } from 'react';
import {
  BookOpen, Search, Loader2, AlertCircle, ChevronRight, Pin, Eye,
  ArrowLeft, Plus, Edit2, Trash2, X, Save, Tag, Clock, FileText,
  TrendingUp, Headphones, Factory, Package, ShoppingCart, Users,
  IndianRupee, Sparkles, Filter, Globe,
} from 'lucide-react';
import { api } from '@/utils/api';

// ==================== TYPES ====================

interface KnowledgeBase {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  departmentId: string | null;
  visibility: string;
  isActive: boolean;
  sortOrder: number;
  _count?: { articles: number };
}

interface KBArticle {
  id: string;
  title: string;
  slug: string;
  contentHtml: string;
  excerpt: string | null;
  category: string | null;
  tags: string[];
  knowledgeBaseId: string;
  parentId: string | null;
  sortOrder: number;
  isPublished: boolean;
  isPinned: boolean;
  views: number;
  createdAt: string;
  updatedAt: string;
  knowledgeBase?: { id: string; name: string; icon: string | null };
}

const ICON_MAP: Record<string, any> = {
  TrendingUp, Headphones, Factory, Package, ShoppingCart, Users,
  IndianRupee, BookOpen, FileText, Sparkles, Globe,
};

// ==================== MAIN PAGE ====================

export function KnowledgeBasePage() {
  const [kbs, setKbs] = useState<KnowledgeBase[]>([]);
  const [selectedKb, setSelectedKb] = useState<KnowledgeBase | null>(null);
  const [articles, setArticles] = useState<KBArticle[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<KBArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<KBArticle[]>([]);
  const [searching, setSearching] = useState(false);

  const fetchKbs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/knowledge-base');
      setKbs(res.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load knowledge bases');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchKbs(); }, [fetchKbs]);

  const fetchArticles = async (kb: KnowledgeBase) => {
    setSelectedKb(kb);
    setSelectedArticle(null);
    setLoadingArticles(true);
    try {
      const res = await api.get(`/knowledge-base/${kb.id}`);
      setArticles(res.data.data?.articles || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load articles');
    } finally {
      setLoadingArticles(false);
    }
  };

  const fetchArticle = async (kbId: string, articleId: string) => {
    try {
      const res = await api.get(`/knowledge-base/${kbId}/articles/${articleId}`);
      setSelectedArticle(res.data.data);
    } catch {}
  };

  const handleSearch = async (q: string) => {
    setSearch(q);
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get(`/knowledge-base/search/articles?q=${encodeURIComponent(q)}`);
      setSearchResults(res.data.data || []);
    } catch {}
    setSearching(false);
  };

  const visibilityBadge = (v: string) => {
    const colors: Record<string, string> = {
      company: 'bg-green-100 text-green-700',
      department: 'bg-blue-100 text-blue-700',
      restricted: 'bg-red-100 text-red-700',
    };
    return <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${colors[v] || 'bg-gray-100 text-gray-600'}`}>{v}</span>;
  };

  // ==================== ARTICLE VIEW ====================
  if (selectedArticle) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <button onClick={() => setSelectedArticle(null)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-[var(--ink-0)] mb-4">
          <ArrowLeft size={16} /> Back to {selectedKb?.name || 'articles'}
        </button>
        <article className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{selectedArticle.title}</h1>
              <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                {selectedArticle.category && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">{selectedArticle.category}</span>}
                <span className="flex items-center gap-1"><Eye size={12} />{selectedArticle.views} views</span>
                <span className="flex items-center gap-1"><Clock size={12} />{new Date(selectedArticle.updatedAt).toLocaleDateString('en-IN')}</span>
                {selectedArticle.isPinned && <span className="flex items-center gap-1 text-amber-600"><Pin size={12} />Pinned</span>}
              </div>
            </div>
          </div>
          {selectedArticle.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {selectedArticle.tags.map(t => (
                <span key={t} className="flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-600">
                  <Tag size={10} />{t}
                </span>
              ))}
            </div>
          )}
          <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: selectedArticle.contentHtml }} />
        </article>
      </div>
    );
  }

  // ==================== ARTICLES LIST ====================
  if (selectedKb) {
    const pinnedArticles = articles.filter(a => a.isPinned);
    const regularArticles = articles.filter(a => !a.isPinned);
    const categories = [...new Set(articles.map(a => a.category).filter(Boolean))] as string[];

    return (
      <div className="p-6">
        <button onClick={() => { setSelectedKb(null); setArticles([]); }} className="flex items-center gap-1 text-sm text-gray-500 hover:text-[var(--ink-0)] mb-4">
          <ArrowLeft size={16} /> All Knowledge Bases
        </button>
        <div className="flex items-center gap-3 mb-6">
          {(() => { const Icon = ICON_MAP[selectedKb.icon || ''] || BookOpen; return <Icon size={28} className="text-[var(--ink-0)]" />; })()}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{selectedKb.name}</h1>
            {selectedKb.description && <p className="text-sm text-gray-500">{selectedKb.description}</p>}
          </div>
          <div className="ml-auto text-sm text-gray-400">{articles.length} articles</div>
        </div>

        {loadingArticles ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-[var(--saffron-600)]" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Categories */}
            {categories.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <span key={cat} className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">{cat}</span>
                ))}
              </div>
            )}

            {/* Pinned */}
            {pinnedArticles.length > 0 && (
              <div>
                <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-700 mb-3">
                  <Pin size={14} /> Pinned Articles
                </h3>
                <div className="grid gap-3 md:grid-cols-2">
                  {pinnedArticles.map(a => (
                    <ArticleCard key={a.id} article={a} onClick={() => fetchArticle(selectedKb.id, a.id)} />
                  ))}
                </div>
              </div>
            )}

            {/* Regular articles grouped by category */}
            {categories.map(cat => {
              const catArticles = regularArticles.filter(a => a.category === cat);
              if (catArticles.length === 0) return null;
              return (
                <div key={cat}>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">{cat}</h3>
                  <div className="grid gap-3 md:grid-cols-2">
                    {catArticles.map(a => (
                      <ArticleCard key={a.id} article={a} onClick={() => fetchArticle(selectedKb.id, a.id)} />
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Uncategorized */}
            {regularArticles.filter(a => !a.category).length > 0 && (
              <div className="grid gap-3 md:grid-cols-2">
                {regularArticles.filter(a => !a.category).map(a => (
                  <ArticleCard key={a.id} article={a} onClick={() => fetchArticle(selectedKb.id, a.id)} />
                ))}
              </div>
            )}

            {articles.length === 0 && (
              <div className="text-center py-16 text-gray-400">
                <FileText size={48} className="mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No articles in this knowledge base yet</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ==================== KB LIST (HOME) ====================
  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-bold text-gray-900">
            <BookOpen size={28} className="text-[var(--ink-0)]" />
            Knowledge Base
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Browse department-specific knowledge bases, SOPs, and guides.
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search across all knowledge bases..."
          value={search}
          onChange={e => handleSearch(e.target.value)}
          className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
        />
        {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />}
      </div>

      {/* Search results */}
      {search.length >= 2 && searchResults.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-gray-600">Search Results ({searchResults.length})</h3>
          {searchResults.map(a => (
            <div
              key={a.id}
              onClick={() => { setSelectedArticle(a); }}
              className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 cursor-pointer hover:bg-gray-50"
            >
              <FileText size={16} className="text-gray-400" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{a.title}</p>
                {a.excerpt && <p className="text-xs text-gray-500 truncate">{a.excerpt}</p>}
              </div>
              {a.knowledgeBase && (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500">{a.knowledgeBase.name}</span>
              )}
              <ChevronRight size={14} className="text-gray-400" />
            </div>
          ))}
        </div>
      )}

      {/* KB grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-[var(--saffron-600)]" />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {kbs.map(kb => {
            const Icon = ICON_MAP[kb.icon || ''] || BookOpen;
            return (
              <div
                key={kb.id}
                onClick={() => fetchArticles(kb)}
                className="rounded-xl border border-gray-200 bg-white p-5 cursor-pointer hover:border-[var(--ink-3)] hover:shadow-md transition-all group"
              >
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-[var(--saffron-50)] p-2.5 text-[var(--ink-0)] group-hover:bg-[var(--saffron-50)] transition-colors">
                    <Icon size={22} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 group-hover:text-[var(--ink-0)]">{kb.name}</h3>
                    {kb.description && <p className="mt-1 text-xs text-gray-500 line-clamp-2">{kb.description}</p>}
                  </div>
                  <ChevronRight size={16} className="text-gray-300 group-hover:text-[var(--saffron-600)] mt-0.5" />
                </div>
                <div className="mt-3 flex items-center gap-2">
                  {visibilityBadge(kb.visibility)}
                  <span className="text-xs text-gray-400">{kb._count?.articles || 0} articles</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && kbs.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <BookOpen size={48} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm">No knowledge bases available for your department</p>
        </div>
      )}
    </div>
  );
}

// ==================== ARTICLE CARD ====================

function ArticleCard({ article, onClick }: { article: KBArticle; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 cursor-pointer hover:border-[var(--ink-3)] hover:shadow-sm transition-all"
    >
      <FileText size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-gray-900 truncate">{article.title}</p>
          {article.isPinned && <Pin size={12} className="text-amber-500 flex-shrink-0" />}
        </div>
        {article.excerpt && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{article.excerpt}</p>}
        <div className="flex items-center gap-2 mt-1.5">
          {article.category && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500">{article.category}</span>}
          <span className="flex items-center gap-0.5 text-[10px] text-gray-400"><Eye size={10} />{article.views}</span>
        </div>
      </div>
      <ChevronRight size={14} className="text-gray-300 mt-1 flex-shrink-0" />
    </div>
  );
}
